import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, BellOff, Send } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { translateError } from '../lib/translate-error';
import { cn } from '../lib/cn';

/** Enable/disable Web Push for THIS device.
 *
 *  Used by both shells, because both audiences get notifications — a platform admin
 *  needs to hear about a failed provisioning as much as a customer needs to hear
 *  about a call.
 *
 *  Every non-actionable state explains itself instead of showing a button that
 *  cannot work: a denied permission cannot be re-requested from script, an insecure
 *  origin cannot register a worker at all, and a server without VAPID keys has
 *  nothing to subscribe against. Rendering "Enable" in those cases would look like a
 *  bug in the app rather than a condition to fix elsewhere. */
export function PushToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { state, busy, enable, disable, sendTest } = usePushNotifications();

  if (state === 'loading') return null;
  // Nothing the user can do about these four, and nothing the app should pretend
  // about — but worth saying, especially `insecure`: over plain http or a bare IP
  // everything looks fine and registration silently fails.
  if (
    state === 'unsupported' ||
    state === 'insecure' ||
    state === 'unconfigured' ||
    state === 'noworker'
  ) {
    return (
      <div className={cn('text-xs text-muted-foreground', className)}>
        {t(`push.${state}`)}
      </div>
    );
  }

  if (state === 'blocked') {
    return (
      <div className={cn('text-xs text-muted-foreground', className)}>{t('push.blocked')}</div>
    );
  }

  const on = state === 'on';

  async function toggle() {
    try {
      await (on ? disable() : enable());
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function test() {
    try {
      await sendTest();
      toast.success(t('push.testSent'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  // Two buttons, each on its own row: the enable/disable toggle, and — only once
  // subscribed, since a test push has nowhere to land otherwise — the self-test.
  return (
    <div className={cn('flex flex-col items-end gap-2', className)}>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm',
          'text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50',
          on && 'text-foreground',
        )}
      >
        {on ? <Bell className="h-4 w-4 shrink-0" /> : <BellOff className="h-4 w-4 shrink-0" />}
        {on ? t('push.on') : t('push.enable')}
      </button>
      {on && (
        <button
          type="button"
          onClick={test}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm',
            'text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50',
          )}
        >
          <Send className="h-4 w-4 shrink-0" />
          {t('push.test')}
        </button>
      )}
    </div>
  );
}
